import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { withAuth } from 'Components/WithAuth';
import { login } from 'Api/ProfileApi';
import { SignInLayout } from 'Features/SignIn/SignInLayout';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { setSecondScreen } from 'State/SecondScreen/SecondScreenActions';
import { setInitialStep, addRedirectUrl } from 'State/Integrations/IntegrationsActions';
import { getUser } from 'Utils/UserCookie';
import { SignInRedirectLoader } from 'Features/SignIn/SignInRedirectLoader';
import { signInRedirect } from 'Utils/SignInHelpers';
import { DeviceRegistrationModal } from 'Features/DeviceRegistrationModal';
import { ALT_CAPTCHA_ERROR_MESSAGE } from 'Utils/Constants';

const INCORRECT_LOGIN_ERROR_MSG = 'Incorrect email address or password. Please try again.';
const EXCEED_LOGIN_ERROR_MSG = 'Maximum login attempts exceeded. Try again later...';

export class SignInComponent extends Component {
  state = {
    isExternalRedirect: false,
    redirectTo: undefined,
    showRedirectLoading: false,
    redirectPath: null,
    redirectSearch: null,
    redirectState: {},
    showModal: false,
    deviceRegInfo: {},
  };

  async componentDidMount() {
    const { location, onSignInSuccess, setInitialStep, addRedirectUrl, setSecondScreen, user } = this.props;
    const { pathname } = location;
    const isDeviceActivation = pathname.includes('tv');

    if (isDeviceActivation) {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const device = urlParams.get('device');
      const secondScreen = {
        isDeviceActivation,
        code,
        device,
      };
      setSecondScreen(secondScreen);
    }

    trackEvent(
      TRACKING_ENUMS.EVENT_LOGIN_STARTED,
      { setPageName: 'profile_sign_in_landing' },
      { dcg_profile_id: user.profileId }
    );

    if (user.isAuthenticated) {
      const userData = getUser();
      const hasEntitlement = await onSignInSuccess(userData);

      trackEvent(
        TRACKING_ENUMS.EVENT_LOGIN_COMPLETE,
        { page_profile_type: 'general', page_start_source: 'general' },
        { dcg_profile_id: user.profileId }
      );

      const { redirectPath, redirectSearch, redirectState, showRedirectLoading, redirectStep, redirectUrl } =
        await signInRedirect(hasEntitlement, location);

      if (redirectStep) setInitialStep(redirectStep);
      if (redirectUrl) addRedirectUrl(redirectUrl);
      this.setState({
        redirectPath,
        redirectSearch,
        redirectState,
        showRedirectLoading,
      });
    }
  }

  handleFormSubmit = async ({ email, password }) => {
    const {
      onSignInSuccess,
      setInitialStep,
      addRedirectUrl,
      defaultConfig: { signInCaptchaEnabled, signInCaptchaSiteKey },
      location,
    } = this.props;

    try {
      let recaptchaToken;
      if (signInCaptchaEnabled && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(signInCaptchaSiteKey, { action: 'submit' });
      }
      const user = await login({ email, password, recaptchaToken });
      const hasEntitlement = await onSignInSuccess(user);
      const { redirectPath, redirectSearch, redirectState, showRedirectLoading, redirectStep, redirectUrl } =
        await signInRedirect(hasEntitlement, location);

      if (redirectStep) setInitialStep(redirectStep);
      if (redirectUrl) addRedirectUrl(redirectUrl);
      this.setState({
        redirectPath,
        redirectSearch,
        redirectState,
        showRedirectLoading,
      });
    } catch (error) {
      let message = '';
      const { statusCode, errorCode, ...errorData } = error;

      const errorType = statusCode >= 500 ? 'Server-side' : 'Other';
      const errorMsg = `${statusCode >= 500 ? 'HTTP 500 Error' : error.detail || error.message}: ${statusCode}`;

      message =
        errorCode === 429
          ? EXCEED_LOGIN_ERROR_MSG
          : error.message?.includes('reCaptcha')
          ? ALT_CAPTCHA_ERROR_MESSAGE
          : INCORRECT_LOGIN_ERROR_MSG;

      if (statusCode === 409) {
        const devices = Object.values(errorData);
        this.setState({
          ...this.state,
          deviceRegInfo: {
            login: { email, password },
            devices,
          },
          showModal: true,
        });
        return;
      }

      trackEvent(
        TRACKING_ENUMS.EVENT_LOGIN_ERROR,
        {
          page_error_description: errorMsg,
          page_error_type: errorType,
        },
        {
          error_type: errorType,
          error_message: errorMsg,
        }
      );

      return onSubmitReactFormError(message || error);
    }
  };

  closeDeviceModal = () => {
    this.setState({ showModal: false, deviceRegInfo: null });
    trackEvent(TRACKING_ENUMS.EVENT_BUTTON_LINK_SELECTED, {
      page_button_text: 'exit',
      page_name: 'account:limit',
      page_type: 'device limit',
    });
  };

  handleDeviceModalSubmit = () => {
    this.setState({ showModal: false });
    // re-login after deleting one or more device sessions
    this.handleFormSubmit(this.state.deviceRegInfo.login);
  };

  render() {
    const { redirectPath, redirectSearch, redirectState, showRedirectLoading } = this.state;
    const {
      routes,
      locale,
      defaultConfig,
      isGifting,
      secondScreen: { isDeviceActivation },
    } = this.props;
    if (redirectPath) {
      return (
        <Navigate
          replace
          to={{
            pathname: redirectPath,
            search: redirectSearch,
          }}
          state={redirectState}
        />
      );
    }

    if (showRedirectLoading) {
      return <SignInRedirectLoader title="Sign In Success" secondaryText="Taking you to your page.." />;
    }

    return (
      <>
        <SignInLayout
          handleFormSubmit={this.handleFormSubmit}
          locale={locale}
          routes={routes}
          isGifting={isGifting}
          isDeviceActivation={isDeviceActivation}
          defaultConfig={defaultConfig}
        />
        {this.state.showModal && (
          <DeviceRegistrationModal
            onClose={this.closeDeviceModal}
            onSuccess={this.handleDeviceModalSubmit}
            devices={this.state.deviceRegInfo.devices}
            loginInfo={this.state.deviceRegInfo.login}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = ({
  user,
  appConfig: { defaultConfig, strings, routes },
  purchaseMode: { isGifting = false } = {},
  secondScreen,
}) => ({
  user,
  defaultConfig,
  locale: strings,
  routes,
  isGifting,
  secondScreen,
});

const mapDispatchToProps = {
  setSecondScreen,
  setInitialStep,
  addRedirectUrl,
};

const ConnectedSignInContainer = connect(mapStateToProps, mapDispatchToProps)(SignInComponent);

export const SignInContainer = withAuth(ConnectedSignInContainer);
